import React, { useRef, useState } from 'react'
import { withFormApi, withFormState } from 'informed'
import { compose, branch } from 'recompose'
import set from 'lodash/set'
import wrapField from '../shared/wrapField'
import { StepButton } from './StyledNumber'
import useLocale from 'hooks/useLocale'


// need to convertNumber
const getDecimalSign = (locale) => {
	const { format } = new Intl.NumberFormat(locale)
	const [, decimalSign] = /^0(.)1$/.exec(format(0.1))
	return decimalSign
}
// A func that will convert a value to a number in certain cases.
// Also for the case where precision === 3 or bellow and the first numbers after the separator === 0.
const convertingFractionalNumbers = (value = '', funcName = '', precision = 0, locale) => {
	const decimalSign = getDecimalSign(locale)
	const fractionalNumbers = String(value).split('.')
	const fractionalLength = fractionalNumbers[1]?.length
	const condition = (fractionalLength === 1 && fractionalNumbers[1]?.includes('0')) || (fractionalLength === 2 && fractionalNumbers[1]?.includes('00'))
	if(funcName === 'convertNumber') {
		if(condition) {
			return Number.parseFloat(value).toFixed(precision)
		}
		return +value
	} else if(funcName === 'localeNumber') {
		if(condition) {
			return `${fractionalNumbers[0]}${decimalSign}${fractionalNumbers[1]}`
		}
		return Number(value).toLocaleString(locale)
	} else if(funcName === 'handleChange') {
		const decimalValue = String(value).split(decimalSign)[1]
		const decimalValueLength = decimalValue?.length
		const convertToNum = (decimalValueLength === 1 && decimalValue?.includes('0')) || (decimalValueLength === 2 && decimalValue?.includes('00'))
		if(convertToNum) {
			return true
		}
		return false
	}
}

// inverse function to toLocaleString
export const convertNumber = (num, locale, precision) => {
	if((num && typeof num === 'number') || num === 0) {
		return(num)
	}else if(num) {
		const decimalSign = getDecimalSign(locale)
		const number = num
			.replace(new RegExp(`[^${decimalSign}\\d-]`, 'g'), '')
			.replace(decimalSign, '.')
		return convertingFractionalNumbers(number, 'convertNumber', precision, locale)
	}
}

const Field = props => {
	const {
		smartSubmit,
		withControls,
		formState,
		formApi,
		field,
		fieldState,
		fieldApi,
		stepProp: step = 1,
		forwardedRef,
		noForm,
		precision = 0,
		resolvePlaceholder,
		enableNegative,
		toFixed,
		onBlur,
		onFocus,
		onChange,
		onKeyDown,
		value,
		withLocale,
		...rest
	} = props

	const [isFocused, setIsFocused] = useState(false)
	const [inputValue, setInputValue] = useState(null)
	const isForm = props.fieldApi && props.fieldState
	const inputRef = useRef()
	const locales = useLocale()

	const changeValue = incr => e => {
		const oldVal = (isForm ? fieldState.value : inputRef.current.value) || 0
		// make number from string
		const oldNumbVal = convertNumber(oldVal, locales, precision)
		let newVal = incr ? oldNumbVal + step : oldNumbVal - step
		newVal = Math.round(newVal * Math.pow(10, precision)) / Math.pow(10, precision)	// round this value as 0.1 + 0.2 === 0.3 is false
		if(newVal < 0) {
			return
		}
		if(isForm) {
			fieldApi.setValue(newVal)
		}
		if(onChange) {
			onChange(newVal)
		}
		if(smartSubmit) {
			smartSubmit.submit(e)
		}
	}

	const handleFocus = e => {
		setIsFocused(true)
	}
	const handleBlur = e => {
		setIsFocused(false)
		if(isForm && smartSubmit) {
			smartSubmit.submit(e)
		}
		if(onBlur) {
			onBlur(e)
		}
	}
	const handleKeyDown = e => {
		const decimalSign = getDecimalSign(locales)
		// approve to use '.' when it is the decimal sign
		if ((e.keyCode === 190 || e.keyCode === 110) && decimalSign === '.') {
			e.preventDefault()
			inputRef.current.value += '.'
		}
		// approve to use ',' when it is the decimal sign
		if(e.keyCode === 188 && decimalSign === ',') {
			e.preventDefault()
			inputRef.current.value += ','
		}
		// disable 'e', ',', '+', '-'
		const disabledCodes = [69, 187]
		if(!enableNegative) {
			disabledCodes.push(189)
		}
		if(disabledCodes.includes(e.keyCode)) {
			e.preventDefault()
		}
		if(e.keyCode === 13 && inputRef.current) {
			// blur on enter key press
			inputRef.current.blur()
		}
		if(onKeyDown) {
			onKeyDown(e)
		}
	}

	const handleChange = e => {
		// make number from string
		const eValue = e.target.value
		const isAndroid = navigator.userAgent.match(/Android/i)
		// Workaround to enter '.' and ',' on Android OS because e.keyCode is always 229 in onKeyDown event
		if(isAndroid && (eValue[eValue.length - 1] === '.' || eValue[eValue.length - 1] === ',')) {
			setInputValue(eValue)
			return
		}
		const isZeroAfterDelimiter = convertingFractionalNumbers(eValue, 'handleChange', precision, locales)
		let newValue = e.target.value ? convertNumber(eValue, locales, precision) : null
		if(newValue && (precision || precision === 0)) {
			const valStr = newValue.toString()
			const ind = valStr.indexOf('.')
			if(ind > -1) {
				if(precision === 0 || valStr.slice(ind + 1).length > precision) {
					return
				}
			}
		}
		if(isForm) {
			if(newValue === null) {
				// Prevent deleting field from form values if it's setted to null
				const newFormVals = set(formState.values, field, newValue)
				formApi.setValues(newFormVals)
			}
			if(isZeroAfterDelimiter) {
				setInputValue(eValue)
				if(+newValue !== fieldState.value) {
					fieldApi.setValue(+newValue)
				}
			} else {
				setInputValue(null)
				fieldApi.setValue(newValue)
			}
			fieldApi.setTouched()
			if(rest.onChange) {
				rest.onChange(e)
			}
			if(smartSubmit) {
				smartSubmit.delay(e)
			}
		}
		if(onChange) {
			if(isZeroAfterDelimiter) {
				const currentValue = inputRef.current.value
				setInputValue(eValue)
				if(+newValue !== currentValue) {
					onChange(+newValue)
				}
			} else {
				setInputValue(null)
				onChange(newValue)
			}
		}
	}

	const getValue = () => {
		let output = ''
		if(isForm && (fieldState.value || fieldState.value === 0)) {
			output = fieldState.value
		} else if(value || value === 0) {
			output = value
		}
		return output
	}

	const addZeros = (number, prec, local) => {
		const decimalSign = getDecimalSign(local)
		let [wholePart, fractionalPart = ''] = number.split(decimalSign)
		if(fractionalPart.length < prec) {
			while(fractionalPart.length < prec) {
				fractionalPart += '0'
			}
			return([wholePart, fractionalPart].join(decimalSign))
		}
		return number
	}

	const localeNumber = (numb) => convertingFractionalNumbers(numb, 'localeNumber', precision, locales)

	const prepareToShow = (startValue = '', prec, local) => {
		let output = ''
		if(!isFocused && startValue && prec) {
			// here we are fixing value to number of digits after comma
			// only when this field is not focused
			const shortenedNumb = Number(startValue).toFixed(prec)
			const localeNumb = localeNumber(shortenedNumb)
			const withZerosNumb = addZeros(localeNumb, prec, local)
			return withZerosNumb
		}
		if(startValue || startValue === 0) {
			output = localeNumber(startValue)
		}
		return output
	}
	return (
		<>
			{withControls &&
				<>
					<StepButton increment onClick={changeValue(true)}/>
					<StepButton decrement onClick={changeValue(false)}/>
				</>
			}
			<input
				ref={inputRef}
				placeholder={resolvePlaceholder('number')}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onChange={handleChange}
				step={step}
				value={inputValue !== null ? inputValue : withLocale ? prepareToShow(getValue(), precision, locales) : getValue()}
				{...rest}
				type='text'
			/>
		</>
	)
}

export default compose(
	wrapField,
	branch(({ noForm }) => !noForm,
		withFormApi
	),
	branch(({ noForm }) => !noForm,
		withFormState
	)
)(Field)
